/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ParticipantType, Bid } from '../../types';
import { apiClient } from '../../services/api';
import './Dashboard.css';

type FilterType = 'all' | 'not_submitted' | 'submitted' | 'accepted';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [recentBids, setRecentBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');

  useEffect(() => {
    fetchRecentBids();
  }, []);

  const fetchRecentBids = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getRecentlyModifiedBids();
      if (response.success && response.bids) {
        setRecentBids(response.bids);
      }
    } catch (error) {
      console.error('Error fetching recent bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBids = (bids: Bid[]): Bid[] => {
    if (filter === 'all') return bids;

    return bids.filter(bid => {
      switch (filter) {
        case 'not_submitted':
          return !bid.submitted;
        case 'submitted':
          return bid.submitted === true;
        case 'accepted':
          return bid.accepted === true;
        default:
          return true;
      }
    });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'No date set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="dashboard">
      <h1>Welcome to Bid Generator</h1>
      <div className="dashboard-card">
        <h2>User Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="label">Username:</span>
            <span className="value">{currentUser?.username}</span>
          </div>
          <div className="info-item">
            <span className="label">Name:</span>
            <span className="value">
              {currentUser?.firstName} {currentUser?.lastName}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Role:</span>
            <span className="value">{currentUser?.type}</span>
          </div>
          {currentUser?.company && (
            <div className="info-item">
              <span className="label">Company:</span>
              <span className="value">{currentUser.company.name}</span>
            </div>
          )}
          {currentUser?.manager && (
            <div className="info-item">
              <span className="label">Manager:</span>
              <span className="value">
                {currentUser.manager.firstName} {currentUser.manager.lastName}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-card">
        <div className="bids-header">
          <h2>All Bids</h2>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filter === 'not_submitted' ? 'active' : ''}`}
              onClick={() => setFilter('not_submitted')}
            >
              Not Submitted
            </button>
            <button
              className={`filter-btn ${filter === 'submitted' ? 'active' : ''}`}
              onClick={() => setFilter('submitted')}
            >
              Submitted
            </button>
            <button
              className={`filter-btn ${filter === 'accepted' ? 'active' : ''}`}
              onClick={() => setFilter('accepted')}
            >
              Accepted
            </button>
          </div>
        </div>
        {loading ? (
          <p>Loading bids...</p>
        ) : filterBids(recentBids).length > 0 ? (
          <div className="recent-bids-list">
            {filterBids(recentBids).slice(0, 5).map((bid) => {
              // Admins and managers view bids in read-only mode
              // Users navigate to edit mode for their own bids
              const navigateTo = (currentUser?.type === ParticipantType.ADMIN || currentUser?.type === ParticipantType.MANAGER)
                ? `/bids/${bid.id}/view`
                : `/bids/${bid.id}/edit`;

              return (
                <div
                  key={bid.id}
                  className="recent-bid-item"
                  onClick={() => navigate(navigateTo)}
                >
                  <div className="bid-info">
                    <h4 className="bid-name">
                      {bid.name}
                      {bid.accepted && <span className="status-badge accepted">✓ Accepted</span>}
                      {bid.submitted && !bid.accepted && <span className="status-badge submitted">✓ Submitted</span>}
                    </h4>
                    {bid.author && (currentUser?.type === ParticipantType.ADMIN || currentUser?.type === ParticipantType.MANAGER) && (
                      <span className="bid-author">
                        by {bid.author.firstName} {bid.author.lastName}
                      </span>
                    )}
                  </div>
                  <div className="bid-dates">
                    <span className="due-date">
                      <strong>Due:</strong> {formatDate(bid.dueDate)}
                    </span>
                    <span className="modified-date">
                      <strong>Modified:</strong> {formatDate(bid.lastModified)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p>No bids found matching the current filter.</p>
        )}
      </div>

      <div className="dashboard-cards">
        <div className="dashboard-card">
          <h3>Quick Actions</h3>
          <ul className="action-list">
            <li>
              <Link to="/bids">View All Bids</Link>
            </li>
            <li>
              <Link to="/components">View All Components</Link>
            </li>
            <li>
              <Link to="/calendar">Company Calendar</Link>
            </li>
            {currentUser?.type === ParticipantType.EXECUTIVE && (
              <li>
                <Link to="/executive/purchase-credits">Purchase Credits</Link>
              </li>
            )}
            {(currentUser?.type === ParticipantType.MANAGER ||
              currentUser?.type === ParticipantType.ADMIN) && (
              <li>
                <Link to="/bids/create">Create New Bid</Link>
              </li>
            )}
            {currentUser?.type === ParticipantType.ADMIN && (
              <>
                <li>
                  <Link to="/participants">Manage Participants</Link>
                </li>
                <li>
                  <Link to="/participants/create">Create/Edit User</Link>
                </li>
                <li>
                  <Link to="/companies">Manage Companies</Link>
                </li>
              </>
            )}
            {currentUser?.type === ParticipantType.MANAGER && (
              <li>
                <Link to="/participants/user/create">Create/Edit User</Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
